import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { SchedulingTeamDispatchContact } from "./dispatch.service";

// Driver Assignments Tab interfaces
export interface DriverDayTripInfo {
  id: string;
  clientName: string | null;
  billToName: string | null;
  loadLocationName: string | null;
}

export interface NoteIconState {
  hasIcon: boolean;
  resolved: boolean;
  tooltip: string;
}

export interface DriverDayData {
  date: Date;
  isTimeOff: boolean;
  timeOffType: string | null;
  timeOffEventId: string | null;
  isEstimate34h?: boolean;
  tripCount: number;
  boardName: string | null;
  trips: DriverDayTripInfo[];
  notes?: { id: string; type: string; note: string; status: string }[];
  // Pre-computed note icon state
  accidentIcon: NoteIconState;
  incidentIcon: NoteIconState;
  breakdownIcon: NoteIconState;
}

export interface DriverAssignmentRow {
  driver: any; // SchedulingTeamDriverAssignment from dispatch.service.ts
  schedulingTeamId: string;
  schedulingTeamName: string;
  clientId: string;
  clientName: string;
  dispatchContacts: SchedulingTeamDispatchContact[];
  days: DriverDayData[];
  // Pre-computed display values to avoid recalculation on every change detection cycle
  displayName: string;
  pictureUrl: string;
  openNoteCount: number;
  // Pre-computed search match state - updated by applyFilters()
  matchedFieldSet: Set<string>;
}

export interface ClientSchedulingTeamGroup {
  clientId: string;
  clientName: string;
  schedulingTeams: any[]; // SchedulingTeamForAssignments from dispatch.service.ts
}

@Injectable({
  providedIn: "root",
})
export class DispatchScheduleService {
  constructor(private http: HttpClient) {}

  /**
   * Create a new driver time off event
   */
  createDriverTimeOff(
    driverId: string,
    startDate: Date,
    endDate: Date,
    eventType: string,
    notes: string
  ): Observable<any> {
    return this.http.post(`/api/driver/events/create`, {
      driverId: driverId,
      start: startDate,
      end: endDate,
      eventType: eventType,
      notes: notes,
    });
  }

  /**
   * Update an existing driver time off event
   */
  updateDriverTimeOff(
    eventId: string,
    driverId: string,
    startDate: Date,
    endDate: Date,
    eventType: string,
    notes: string
  ): Observable<any> {
    return this.http.patch(`/api/driver/events/update`, {
      id: eventId,
      driverId: driverId,
      start: startDate,
      end: endDate,
      eventType: eventType,
      notes: notes,
    });
  }

  /**
   * Delete a driver time off event
   */
  deleteDriverTimeOff(eventId: string): Observable<any> {
    return this.http.delete(`/api/driver/events/delete/${eventId}`);
  }

  /**
   * Get a specific driver time off event
   */
  getDriverTimeOffEvent(eventId: string): Observable<any> {
    return this.http.get(`/api/driver/event/${eventId}`);
  }

  /**
   * Manually trigger an Avatar API sync
   */
  triggerAvatarSync(): Observable<any> {
    return this.http.post(`/api/avatar/sync`, {});
  }
}
